import React, { useCallback, useEffect, useState } from "react";
import axios from "../../api";
import {
  Container,
  Paper,
  Box,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
  Chip,
  CircularProgress,
  Tabs,
  Tab,
  Alert,
  Link,
} from "@mui/material";
import { FactCheck, Download, Refresh } from "@mui/icons-material";
import { useNavigate, Link as RouterLink } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import AdminLayout from "../AdminLayout";
import {
  unverifiedInventoryPath,
  leaverInventoryPath,
} from "../../utils/deepLinks";

function ComplianceReport({ onLogout }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(0);
  const [data, setData] = useState(null);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get("/assets/reports/compliance");
      setData(res.data.result || null);
    } catch (err) {
      toast.error("Failed to load compliance report.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const handleLogout = () => {
    if (onLogout) return onLogout();
    localStorage.clear();
    navigate("/login");
  };

  const unverified = data?.unverifiedAssigned || [];
  const leavers = data?.leaversHolding || [];

  const exportCsv = () => {
    if (tab === 0) {
      if (!unverified.length) return toast.info("Nothing to export.");
      const cols = [
        "assetTag",
        "serialNumber",
        "model",
        "category",
        "status",
        "assignedEmail",
        "verificationStatus",
        "dueDate",
      ];
      const lines = [
        cols.join(","),
        ...unverified.map((r) =>
          [
            r.assetTag,
            r.serialNumber,
            r.model,
            r.category,
            r.status,
            r.assignedTo?.email,
            r.verification?.status || "Unknown",
            r.verification?.dueDate
              ? new Date(r.verification.dueDate).toISOString().slice(0, 10)
              : "",
          ]
            .map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`)
            .join(",")
        ),
      ];
      download("compliance-unverified.csv", lines.join("\n"));
    } else {
      if (!leavers.length) return toast.info("Nothing to export.");
      const lines = [
        "email,empName,empId,userStatus,assetCount,assetTags",
        ...leavers.map((r) =>
          [
            r.email,
            r.empName,
            r.empId,
            r.userStatus,
            r.assetCount,
            (r.assets || []).map((a) => a.assetTag || a.serialNumber).join("; "),
          ]
            .map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`)
            .join(",")
        ),
      ];
      download("compliance-leavers.csv", lines.join("\n"));
    }
  };

  const download = (name, body) => {
    const blob = new Blob([body], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = name;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <AdminLayout onLogout={handleLogout}>
      <Container maxWidth="xl" sx={{ py: 3 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            mb: 2,
            flexWrap: "wrap",
          }}
        >
          <FactCheck color="primary" />
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              Compliance pack
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Unverified assigned holdings and leavers still holding assets.
            </Typography>
          </Box>
          <Button startIcon={<Refresh />} onClick={fetchReport} disabled={loading}>
            Refresh
          </Button>
          <Button variant="outlined" startIcon={<Download />} onClick={exportCsv}>
            Export CSV
          </Button>
        </Box>

        {data?.summary && (
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
            <Chip
              color="warning"
              label={`Unverified assigned: ${data.summary.unverifiedAssigned}`}
            />
            <Chip
              color="error"
              label={`Leavers holding: ${data.summary.leaversHolding} (${data.summary.leaversAssetCount} assets)`}
            />
          </Box>
        )}

        <Paper variant="outlined">
          <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ px: 1 }}>
            <Tab label={`Unverified assigned (${unverified.length})`} />
            <Tab label={`Leavers holding (${leavers.length})`} />
          </Tabs>

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress />
            </Box>
          ) : tab === 0 ? (
            <Box sx={{ overflowX: "auto" }}>
              {!unverified.length ? (
                <Alert severity="success" sx={{ m: 2 }}>
                  No unverified assigned assets.
                </Alert>
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Asset</TableCell>
                      <TableCell>Category</TableCell>
                      <TableCell>Assigned to</TableCell>
                      <TableCell>Verification</TableCell>
                      <TableCell>Due</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {unverified.map((r) => {
                      const inventoryUrl = unverifiedInventoryPath({
                        ...r,
                        _id: null, // prefer filtered list for "Open in Inventory"
                      });
                      return (
                      <TableRow key={r._id} hover>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {r.assetTag || r.serialNumber || r.model || "—"}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {r.serialNumber}
                          </Typography>
                          <Link
                            component={RouterLink}
                            to={unverifiedInventoryPath(r)}
                            variant="caption"
                            display="block"
                          >
                            Open asset
                          </Link>
                        </TableCell>
                        <TableCell>{r.category || "—"}</TableCell>
                        <TableCell>{r.assignedTo?.email || "—"}</TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={r.verification?.status || "Unknown"}
                            color={
                              r.verification?.status === "Overdue" ? "error" : "warning"
                            }
                          />
                        </TableCell>
                        <TableCell>
                          {r.verification?.dueDate
                            ? new Date(r.verification.dueDate).toLocaleDateString()
                            : "—"}
                        </TableCell>
                        <TableCell align="right">
                          <Button
                            component={RouterLink}
                            to={inventoryUrl}
                            size="small"
                            variant="outlined"
                          >
                            Open in Inventory
                          </Button>
                        </TableCell>
                      </TableRow>
                    );})}
                  </TableBody>
                </Table>
              )}
            </Box>
          ) : (
            <Box sx={{ overflowX: "auto" }}>
              {!leavers.length ? (
                <Alert severity="success" sx={{ m: 2 }}>
                  No inactive/missing users holding assets.
                </Alert>
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Person</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Assets</TableCell>
                      <TableCell>Tags</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {leavers.map((r) => (
                      <TableRow key={r.email} hover>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {r.empName || r.email}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {r.email}
                            {r.empId ? ` · ${r.empId}` : ""}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip size="small" color="error" label={r.userStatus} />
                        </TableCell>
                        <TableCell>{r.assetCount}</TableCell>
                        <TableCell>
                          {(r.assets || [])
                            .map((a) => a.assetTag || a.serialNumber || a.model)
                            .filter(Boolean)
                            .join(", ")}
                        </TableCell>
                        <TableCell align="right">
                          <Button
                            component={RouterLink}
                            to={leaverInventoryPath(r)}
                            size="small"
                            variant="outlined"
                          >
                            View assets
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </Box>
          )}
        </Paper>
      </Container>
      <ToastContainer position="top-center" autoClose={2800} />
    </AdminLayout>
  );
}

export default ComplianceReport;
